import * as React from "react";
import {
  Box,
  Card,
  Typography,
  Divider,
  LinearProgress,
  useTheme,
  IconButton,
  Stack, Tabs, Tab,
  InputBase,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Link,
  useMediaQuery, Collapse, CircularProgress, ButtonBase
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { fetchData } from "assets/utils";
import SeriesIcon from "components/SeriesIcon";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from '@mui/icons-material/Person';
import { disp_rank_short, shortenUsername } from "assets/utils";
import { useSimulationData } from "assets/utils";
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useHead2Head } from "assets/utils";
import { WinIcon } from "assets/Icons";
import { LoseIcon } from "assets/Icons";
import AddIcon from '@mui/icons-material/Add';
import { useSearch } from "assets/utils";
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import ShadowBox from "components/ShadowBox";
import OutlinedBox from "components/OutlinedBox";
import {getBoxShadow} from "components/ShadowBox";
import { getFirstInitialAndLastName, formatDateDifference } from "assets/utils";
import { useTranslation, Trans } from "react-i18next";
import i18n from "i18n";

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

export function BoxTitle({label}) {
    return <Box sx={{ width: "75px", fontSize: 14, fontWeight: "bold", position: "absolute", mt: "-22px", lineHeight: "20px", ml: 2, backgroundColor: (theme) => theme.palette.primary.main, color: "white", textAlign: "center", borderRadius: 3, alignSelf: "flex-start" }} elevation={0}>
            {label}
        </Box>
}

export function ActivityTab({ labels, value, setValue, sx }) {
  return <Tabs
      centered
      value={value}
      textColor="inherit"
      indicatorColor="inherit"
      onChange={(event, newValue) => setValue(newValue)}
      aria-label="basic tabs example"
      sx={{
        borderRadius: 2,
        backgroundColor: "primary.main",
        boxShadow: getBoxShadow(),
        mt: 2,
        mb: 1,
        "& .MuiTab-root": {
          color: "rgba(255,255,255,0.7)",
        },
        "& .Mui-selected": {
          color: "#fff",
        },
        "& .MuiTabs-indicator": {
          height: 3,
          backgroundColor: "#fff",
        },
        ...sx
      }}
    >
      {labels.map((label, index) => (
        <Tab key={index} label={label} />
      ))}
    </Tabs>
}

export default function ActivityCard({ activity, name, name_club, rankSingles, rankDoubles }) {
  const { t } = useTranslation();
  const singles_activity = activity?.singles ?? [];
  const doubles_activity = activity?.doubles ?? [];

  const [value, setValue] = React.useState(0);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [dialogData, setDialogData] = React.useState(null);

  React.useEffect(() => {
    if (singles_activity.length > 0) {
      setValue(0);
    } else if (doubles_activity.length > 0) {
      setValue(1);
    } else {
      setValue(0); // default to singles tab if no activity, but could be anything since both are empty
    }
  }, [singles_activity, doubles_activity]);

  return (
    <>

    <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mt: 6, mb: 2}}>
      <Typography variant="h6" fontWeight="bold" color="text.primary_bold" component="div" id="matches">
      {t("activityCard.upcomingMatches")}
      </Typography>
      <IconButton aria-label="Example" color="primary" onClick={() => setDialogOpen(true)}>
        <InfoIcon color="primary" />
      </IconButton>
    </Box>
    <ActivityCardDialog open={dialogOpen} setOpen={setDialogOpen} />

    <ActivityTab labels={[t("activityCard.tabSingles", {count: singles_activity.length}), t("activityCard.tabDoubles", {count: doubles_activity.length}), t("activityCard.tabSimulation")]} value={value} setValue={setValue} />

    {value === 0 && singles_activity.length > 0 && <Stack sx={{ gap: 1}}>

      {
        singles_activity.map((item, index) => (
          <React.Fragment key={`${item.sid}-${index}`}>
            <ActivityLine data={item} doubles={false} setDialogData={setDialogData} />
          </React.Fragment>
        ))
      }
    </Stack>}

    {value === 1 && doubles_activity.length > 0 && <Stack sx={{ gap: 1}}>
      {
        doubles_activity.map((item, index) => (
          <React.Fragment key={`${item.sid}-${index}`}>
            <ActivityLine data={item} doubles={true} setDialogData={setDialogData} />
          </React.Fragment>
        ))
      }
    </Stack>}

    {value === 2 && <SimulationFetch name={name} name_club={name_club} rank={rankSingles} setDialogData={setDialogData} />}

    <OutlinedBox sx={{py: 1, px: 1, fontStyle: "italic", textAlign: "center", display: ((value === 0 && singles_activity.length > 0) || (value === 1 && doubles_activity.length > 0) || (value === 2)) ? "none" : "block"}}>
      <Typography variant="body2" color="text.secondary">
      {value === 0 ? t("activityCard.noPlannedSingles") : t("activityCard.noPlannedDoubles")}
      </Typography>
    </OutlinedBox>

    <H2HDialog open={dialogData != null} onClose={() => setDialogData(null)} dialogData={dialogData}/>

    </>
  );
}

function SimulationFetch({ name, name_club, rank, setDialogData }) {
    const { t } = useTranslation();
    const playerId = parseInt(useParams()["*"]);
    const [search, setSearch] = React.useState("");
    const [favoritesSimulation, favoritesFetching] = useSimulationData(playerId, "");
    const [searchSimulation, searchFetching] = useSimulationData(playerId, search);

    const simulation = search.length >= 5 ? searchSimulation : favoritesSimulation;
    const fetching = search.length >= 5 ? searchFetching : favoritesFetching;

    const theme = useTheme();

    const visibleCount = 10;

    return <Stack sx={{ gap: 1}}>

      <OutlinedBox sx={{py: 0.5, px: 1, display: "flex", alignItems: "center", gap: 1}}>
          <InputBase 
            value={search} 
            inputProps={{style: {textTransform: search.length > 0 ? "capitalize" : "none"}}}
            autoComplete='off' 
            onChange={(e) => setSearch(e.target.value)} 
            sx={{width: "100%"}}
            startAdornment={<SearchIcon style={{color: theme.palette.text.secondary, marginRight: 8}} />} 
            placeholder={t("activityCard.searchPlayer")}/>
      </OutlinedBox>

    {(search.length ===0 || search.length >= 5) && simulation
      ? simulation
          .filter(item => item.o_id != playerId)
          .sort((a, b) => a.o_score - b.o_score)
          .slice(0, visibleCount)
          .map((item, index) => (
            <React.Fragment key={`${playerId}-${item.sid}-${index}`}>
              <ActivityLine data={item} doubles={false} name={name} name_club={name_club} rank={rank} setDialogData={setDialogData} />
            </React.Fragment>
          ))
      : null}

      {search.length  < 5 ?
      <OutlinedBox sx={{py: 1, px: 1, fontStyle: "italic", textAlign: "center"}}>
        <Typography variant="body2" color="text.secondary">
          {search.length === 0 ? t("activityCard.addFavorites") : t("activityCard.refineSearchSimulations")}
        </Typography>
      </OutlinedBox>
      : null}

      {search.length >= 5 && !fetching && simulation.length === 0 ?
      <OutlinedBox sx={{py: 1, px: 1, fontStyle: "italic", textAlign: "center"}}>
        <Typography variant="body2" color="text.secondary">
          {t("activityCard.noPlayersFound")}
        </Typography>
      </OutlinedBox>
      : null}

      </Stack>
}


function formatDate(dateInput) {
  const date = new Date(dateInput);

  if (Number.isNaN(date.getTime())) return "";

  const parts = new Intl.DateTimeFormat(i18n.t("dates.locale"), {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (type) =>
    parts.find((part) => part.type === type)?.value.replace(".", "") ?? "";

  const weekday = get("weekday");

  return `${weekday} ${get("day")} ${get("month")} • ` + i18n.t("dates.timeFormat", {hour: get("hour"), minute: get("minute")});
}

function fnv1aHash(str) {
    let hash = 2166136261; // FNV-1a 32-bit offset basis
    for (let i = 0; i < str.length; i++) {
        hash ^= str.charCodeAt(i);
        hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
    }
    return hash >>> 0; // Convert to unsigned 32-bit integer
}

export function BlueColors(str){
    // Step 2: Normalize the hash to a number between 0 and 1
    const normalizedHash = fnv1aHash(str + "apqddve123121q") / 4294967296;

    const value = 35.1 + 50 * normalizedHash;

    return "hsl(206, 100%, " + value + "%)"
}

export function PlayerName({
  id,
  name,
  name_club,
  rank,
  rankColor,
  ageclass,
  right,
  sx,
  color,
  shortenUsernameLength = 20,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const navigateToUser = () => {
    fetchData(id, null, (content) => {
      navigate("/speler/" + id, {
        state: { ...content, ts: new Date().getTime() },
      });
    });
  };

  return (
    <Box
      onClick={() => id && navigateToUser()}
      sx={{
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
        textAlign: right ? "right" : "left",
        cursor: id ? "pointer" : "default",
        ...sx,
      }}
    >
      <Typography
  variant="caption"
  component="div"
  sx={{
    display: "flex",
    alignItems: "baseline",
    justifyContent: right ? "flex-end" : "flex-start",
    width: "100%",
    minWidth: 0,
    color: "text.primary",
  }}
>
        <Box
          component="span"
          title={name}
          sx={{
            flex: "0 1 auto",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontWeight: 700,
            fontSize: 12,
            textAlign: right ? "right" : "left",
          }}
        >
          {shortenUsername(name, shortenUsernameLength) || t("common.unknown")}
        </Box>

        {rank ? (
          <Box
            component="b"
            sx={{
              flexShrink: 0,
              ml: "5px",
              color: rankColor || "#777",
              fontSize: 12,
            }}
          >
            {disp_rank_short(rank, ageclass ? `U${ageclass}` : "U")}
          </Box>
        ) : null}
      </Typography>

      {name_club ? (
        <Typography
          variant="caption"
          component="div"
          noWrap
          title={name_club}
          sx={{
            width: "100%",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            fontWeight: "normal",
            fontSize: 10,
            color: "text.secondary",
          }}
        >
          {name_club}
        </Typography>
      ) : null}
    </Box>
  );
}

export function AccentBar({ color, width=6, borderLeft='2px', borderRight='2px' }) {
  return (
    <Box
      sx={{
        width: width,
        backgroundColor: color,
        flexShrink: 0,
        borderBottomLeftRadius: borderLeft,
        borderBottomRightRadius: borderRight,
        borderTopLeftRadius: borderLeft,
        borderTopRightRadius: borderRight,
      }}
    />
  );
}

function VSSign() {
  const { t } = useTranslation();

  return <Box
      aria-label="versus"
      sx={{
        width: 20,
        height: 20,
        minWidth: 20,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "50%",
        bgcolor: "background.paper",
        color: "text.primary",
        fontSize: 8,
        fontWeight: 700,
        lineHeight: 1,
        userSelect: "none",
      }}

    >

      {t("common.versus")}

    </Box>;
}

export function ActivityLine({ data, doubles, setDialogData, name, rank, name_club, firstPlayerClickable = false }) {
  const theme = useTheme();
  const { t } = useTranslation();

  const nameStr = name ?? data?.my_name;
  const rankStr = rank ?? data?.my_pts;
  const nameClubStr = name_club ?? data?.my_name_club;

  const winPct = Math.round((data.o_score ?? 0.5) * 100);

  const opacity = data.o1_id || data.o_id ? 1 : 0.5;

  const t1Color = theme.palette.primary.main;
  const t2Color = "#ddd";
  const t2ColorText = "#ddd";

    const playerId = parseInt(useParams()["*"]) || data?.my_id;

    const shortenUsernameLength = useMediaQuery(theme.breakpoints.down('sm')) ? 16 : 20;

    const [open, setOpen] = React.useState(false);

  return (
    <ShadowBox
      sx={{
        pt: 1.5,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        overflow: "hidden",
        /*"&:hover": {
          backgroundColor: "action.hover",
        },*/
      }}
    >

        {/*<Avatar
            {...stringAvatarProps(data.o_id, data.o_name)}
        />*/}

        <Stack sx={{justifyContent: "space-between", alignItems: "center", flexDirection: "row", width: "100%", px: 1, pb: 1, minWidth: 0,}}>
            <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, width:"calc(50% - 10px)", flex: "0 0 calc(50% - 10px)", width: "calc(50% - 10px)", maxWidth: "calc(50% - 10px)", minWidth: 0, overflow: "hidden", paddingRight: 1 }}>
              {/*<AccentBar color={t1Color} width={5} borderLeft="0px" />*/}

              <Stack sx={{ alignItems: "flex-start", flexDirection: "column", flex: 1, minWidth: 0, overflow: "hidden", }}>
                <PlayerName id={firstPlayerClickable ? data.my_id : null} name={nameStr} name_club={doubles ? null : nameClubStr} rank={rankStr} color={t1Color} shortenUsernameLength={shortenUsernameLength} />
                {doubles ? <PlayerName id={data.p_id} name={data.p_name} rank={data.p_pts} shortenUsernameLength={shortenUsernameLength} /> : null}
              </Stack>
            </Box>

            <VSSign />

            <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, width:"calc(50% - 10px)", flex: "0 0 calc(50% - 10px)", width: "calc(50% - 10px)", maxWidth: "calc(50% - 10px)", minWidth: 0, overflow: "hidden", paddingLeft: 1 }}>
                <Stack sx={{ alignItems: "flex-end", flexDirection: "column", flex: 1, minWidth: 0, overflow: "hidden", }}>
                  <PlayerName id={doubles ? data.o1_id : data.o_id} name={doubles ? data.o1_name : data.o_name} rank={doubles ? data.o1_pts : data.o_pts} right name_club={data.o_name_club} color={t2ColorText} shortenUsernameLength={shortenUsernameLength} />
                  {doubles && data.o1_id > 0 ? <PlayerName id={data.o2_id} name={data.o2_name} rank={data.o2_pts} right shortenUsernameLength={shortenUsernameLength} /> : null}
                </Stack>
                {/*<AccentBar color={t2Color} width={5} borderRight="0px" />*/}
            </Box>
        </Stack>

        <Box sx={{px: 1, width: "100%", alignItems: "center", display: "flex", flexDirection: "column", mb: 1, mt: 1}}>

        <Stack sx={{justifyContent: "space-between", flexDirection: "row", width: "100%", alignItems: "flex-end", mb: 0.5, opacity: opacity}}>
            <Typography variant="h5" sx={{fontWeight: 700, color: t1Color, width: 60, fontSize: 20, lineHeight: "1em"}}>
                {winPct}%
            </Typography>

            <Typography variant="caption" color="#999" sx={{lineHeight: "1em", fontWeight: "normal"}}>
                {t("activityCard.winPrediction")}
            </Typography>

            <Typography variant="h5" sx={{fontWeight: 700, color: "#aaa", width: 60, fontSize: 20, lineHeight: "1em", textAlign: "right"}}>
                {100 - winPct}%
            </Typography>
        </Stack>

        <LinearProgress variant="determinate" value={winPct} 
            sx={{width: "100%", borderRadius: 5, height: 10,
                    backgroundColor: t2Color,
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: t1Color
                    }, opacity: opacity
            }} 
            
            />

        </Box>


        <Divider sx={{width: "100%", mt: 0.5}} />
        
        <Stack
          sx={{
            justifyContent: "space-between",
            width: "100%",
            flexDirection: "row",
            alignItems: "center",
            px: 1,
            pt: 0.5,
            pb: 0.5,
            gap: 1,
            "&:hover": {
                cursor: "pointer",
            },
          }}

          onClick={() => setOpen((prev) => !prev)}
        >
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.primary.main,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: "1.4em",
              fontSize: 12,
              flexShrink: 1,
              minWidth: 0,
              "&:hover": {
                color: theme.palette.primary.dark,
                cursor: data.url ? "pointer" : "default",
              },
            }}
            onClick={() => {
              if (data.url) {
                window.open(data.url, "_blank");
              }
            }}
          >
            {data.cat && `${data.cat.substring(1).toUpperCase()} ${data.rn_short ?? data.rn ?? ""} • `} {data.title}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", ml: "auto", flexShrink: 0 }}>
            <Typography
              variant="caption"
              sx={{
                color: "#a0a0a0",
                whiteSpace: "nowrap",
                lineHeight: "1.4em",
                fontSize: 12,
                marginLeft: '10px',
              }}
            >
              {data.planned ? `${formatDate(data.planned)}` : ""}
            </Typography>

            {open ? (
              <KeyboardArrowUpIcon sx={{ ml: 0.25, color: "#ccc" }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ ml: 0.25, color: "#ccc" }} />
            )}
          </Box>
        </Stack>
      
    {open && <ActivityCardDetail isSingles={!doubles} myId={playerId} opponentId={data.o_id || data.o1_id} partnerId={data.p_id} opponentPartnerId={data.o2_id} setDialogData={setDialogData} />}
        
    </ShadowBox>

    
  );
}

function ActivityCardDetail({isSingles, myId, opponentId, partnerId, opponentPartnerId, setDialogData}) {
  const { t } = useTranslation();
  const data = useHead2Head(myId, opponentId, partnerId, opponentPartnerId);

    return <Box sx={{width: "100%", mt: 1, pt: 2}}>
      <Collapse in={!data} timeout="auto" unmountOnExit>
        <CircularProgress size={24} sx={{display: "block", mx: "auto", my: 2}} />
      </Collapse>

      <Collapse in={!!data} timeout="auto" unmountOnExit sx={{display: "flex", flexDirection: "column", gap: 1}}>

      <H2HFullSummary data={data} isSingles={isSingles} probabilities={false} />

        <Box sx={{px: 1, pb: 1}}>
        <ButtonBase
                centerRipple={true}
                onClick={() => setDialogData({player1: myId, player2: opponentId, player3: partnerId, player4: opponentPartnerId})}
                sx={{ 
                  display: "flex", justifyContent: "center", borderRadius: 2, backgroundColor: (theme) => theme.palette.primary.main,
                  borderRadius: 2,
                  px: 1.5,
                  py: 1,
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  width: "100%",
                }}
              >
                <LeaderboardIcon sx={{ fontSize: 18, color: 'white' }} />
                <Typography sx={{ fontSize: 12, color: 'white', fontWeight: 600}}>
                  {t("activityCard.viewFullSimulation")}
                </Typography>
              </ButtonBase>
          </Box>

      </Collapse>

    </Box>
}


function ActivityCardDialog({open, setOpen}) {
  const { t } = useTranslation();

  const handleClose = () => {
    setOpen(false);
  };

  

  return <Dialog open={open} onClose={handleClose}>
            <DialogTitle>{t("activityCard.dialogTitle")}</DialogTitle>
            <DialogContent>
            <DialogContentText>
              <Trans i18nKey="activityCard.dialogBody" components={{ br: <br/> }} />
            </DialogContentText>
            
            </DialogContent>
            <DialogActions>
            <Button onClick={handleClose}>{t("common.close")}</Button>
            </DialogActions>
        </Dialog>
}


// H2H

function H2HRecentResults({summary, isSingles}) {
  const { t } = useTranslation();

  return <Box sx={{display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", p: 1}}>
    <Typography variant="caption" color="text.primary_bold" fontWeight="normal">
      {shortenUsername(summary?.name)}
    </Typography>

    <Box sx={{display: "flex", flexDirection: "row", justifyContent: "center", alignItems: "center"}}>
      {summary && summary.activity && (isSingles ? summary.activity.singles : summary.activity.doubles).slice(0,5).map((item, index) => (
          item === 1 ? <WinIcon key={index} /> : item === 0 ? <LoseIcon key={index} /> : null
      ))}

      {summary && (isSingles ? summary.activity.singles.length : summary.activity.doubles.length) === 0 && <Typography variant="caption" color="text.secondary" sx={{mr: '5px'}}>
        {t("common.none")}
      </Typography>}
    </Box>
  </Box>
}

function H2HData({value1, value2, value3, title, suffix, prefix}) {
  return <Stack key={value1} sx={{justifyContent: "space-between", width: "100%", flexDirection: "row", alignItems: "center", px: 1, py: 0.5}}>
    <Typography variant="caption" color="text.primary_bold" sx={{fontWeight: title ? "bold" : "normal", width: "40%"}}>
      {value1}
    </Typography>
    <Typography variant="caption" color={value2 > value3 && !title ? "primary.main" : "text.primary_bold"} sx={{width: "30%", fontWeight: "bold", pl: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"}}>
      {value2 != null ? prefix : ""}{value2}{value2 != null ? suffix : ""}
    </Typography>
    <Typography variant="caption" color={value2 > value3 || title ? "text.primary_bold" : "primary.main"} sx={{width: "30%", fontWeight: "bold", pl: 1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap"}}>
      {value3 != null ? prefix : ""}{value3}{value3 != null ? suffix : ""}
    </Typography>
  </Stack>
}

function H2HSummary({my_summary, o_summary, isSingles}) {

  const { t } = useTranslation();

  const myActivity = isSingles ? my_summary?.activity?.singles : my_summary?.activity?.doubles;
  const oActivity = isSingles ? o_summary?.activity?.singles : o_summary?.activity?.doubles;

  const my_win_pct = myActivity.length > 0 ? Math.round((myActivity.filter(r => r === 1).length / myActivity.length) * 100) : 0;
  const o_win_pct = oActivity.length > 0 ? Math.round((oActivity.filter(r => r === 1).length / oActivity.length) * 100) : 0;

  const recent_win_pct = (isSingles ? my_summary?.singles_nm_60d : my_summary?.doubles_nm_60d) > 0 ? Math.round(((isSingles ? my_summary?.singles_nw_60d : my_summary?.doubles_nw_60d) / (isSingles ? my_summary?.singles_nm_60d : my_summary?.doubles_nm_60d)) * 100) : 0;
  const o_recent_win_pct = (isSingles ? o_summary?.singles_nm_60d : o_summary?.doubles_nm_60d) > 0 ? Math.round(((isSingles ? o_summary?.singles_nw_60d : o_summary?.doubles_nw_60d) / (isSingles ? o_summary?.singles_nm_60d : o_summary?.doubles_nm_60d)) * 100) : 0;

  return <OutlinedBox sx={{m: 1, mt: 0}}>
          <H2HData value1={t("h2h.lastYear")} value2={shortenUsername(my_summary?.name, 5)} value3={shortenUsername(o_summary?.name, 5)} title />
          <Divider sx={{width: "100%"}}/>
          <H2HData value1={t("h2h.matches")} value2={myActivity?.length ?? null} value3={oActivity?.length ?? null} />
          <H2HData value1={t("h2h.winRatio")} value2={my_win_pct} value3={o_win_pct} suffix="%" />
          <H2HData value1={t("h2h.matches60d")} value2={(isSingles ? my_summary?.singles_nm_60d : my_summary?.doubles_nm_60d) ?? null} value3={(isSingles ? o_summary?.singles_nm_60d : o_summary?.doubles_nm_60d) ?? null} />
          <H2HData value1={t("h2h.winRatio60d")} value2={recent_win_pct} value3={o_recent_win_pct} suffix="%" />
          <H2HData value1={t("h2h.ranking")} value2={(isSingles ? my_summary?.singles_pts : my_summary?.doubles_pts) ?? null} value3={(isSingles ? o_summary?.singles_pts : o_summary?.doubles_pts) ?? null} suffix={" " + t("common.pts")} />
          <H2HData value1={t("h2h.playStrength")} value2={my_summary ? (Math.round(isSingles ? my_summary.singles_elo_pts : my_summary.doubles_elo_pts)) : null} value3={o_summary ? (Math.round(isSingles ? o_summary?.singles_elo_pts : o_summary?.doubles_elo_pts)) : null} suffix={" " + t("common.pts")} />
        </OutlinedBox>
}

export function H2HHeader({data, myId, myName, myPts, myClub, partnerId, opponentId, opponentName, opponentPts, opponentClub, opponentPartnerId, isSingles, showPlayers=true}) {
  const theme = useTheme();
  const { t } = useTranslation();
    const winPct = Math.round(((isSingles ? data?.singles_score : data?.doubles_score) ?? 0.5) * 100);
  const t1Color = theme.palette.primary.main;
  const t2Color = "#B3DCFB";
  const t2ColorText = "#8ECAF9";

  const myNameStr = myName ?? data?.my_summary?.name
  const myRank = myPts ?? (isSingles ? data?.my_summary?.singles_pts : data?.my_summary?.doubles_pts);
  const myClubStr = data?.p_summary && !isSingles ? null : myClub ?? data?.my_summary?.club

  const opponentNameStr = opponentName ?? data?.o_summary?.name
  const opponentRank = opponentPts ?? (isSingles ? data?.o_summary?.singles_pts : data?.o_summary?.doubles_pts);
  const opponentClubStr = data?.op_summary && !isSingles ? null : opponentClub ?? data?.o_summary?.club

  const shortenUsernameLength = useMediaQuery(theme.breakpoints.down('sm')) ? 16 : 20;

  return <>{showPlayers && (
          <Stack
            sx={{
              flexDirection: "row",
              alignItems: "center",
              width: "100%",
              minWidth: 0,
              px: 1,
              pb: 1,
              mt: 2,
            }}
          >
            {/* Left side */}
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                gap: 1,

                flex: "1 1 0",
                maxWidth: "calc(50% - 10px)",
                minWidth: 0,
                overflow: "hidden",
                paddingRight: 0.5,
              }}
            >
              {/* <AccentBar color={t1Color} /> */}

              <Stack
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  alignItems: "flex-start",
                  flexDirection: "column",
                }}
              >
                <PlayerName
                  id={myId}
                  name={myNameStr}
                  rank={myRank}
                  name_club={myClubStr}
                  color={t1Color}

                  shortenUsernameLength={shortenUsernameLength}
                />

                {data?.p_summary && !isSingles ? (
                  <PlayerName
                    id={partnerId}
                    name={data.p_summary.name}
                    rank={data.p_summary.doubles_pts}
                    color={t1Color}
                    shortenUsernameLength={shortenUsernameLength}
                  />
                ) : null}
              </Stack>
            </Box>

            <VSSign />

            {/* Right side */}
            <Box
              sx={{
                display: "flex",
                alignItems: "stretch",
                justifyContent: "flex-end",
                gap: 1,

                flex: "1 1 0",
                maxWidth: "calc(50% - 10px)",
                minWidth: 0,
                overflow: "hidden",
                paddingLeft: 0.5,
              }}
            >
              <Stack
                sx={{
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  alignItems: "flex-end",
                  flexDirection: "column",
                }}
              >
                <PlayerName
                  id={opponentId}
                  name={opponentNameStr}
                  rank={opponentRank}
                  name_club={opponentClubStr}
                  right
                  color={t2ColorText}
                  shortenUsernameLength={shortenUsernameLength}
                />

                {data?.op_summary && !isSingles ? (
                  <PlayerName
                    id={opponentPartnerId}
                    name={data.op_summary.name}
                    rank={data.op_summary.doubles_pts}
                    right
                    color={t2ColorText}
                    shortenUsernameLength={shortenUsernameLength}
                  />
                ) : null}
              </Stack>

              {/* <AccentBar color={t2Color} /> */}
            </Box>
          </Stack>
        )}

        <Box sx={{px: 1, width: "100%", alignItems: "center", display: "flex", flexDirection: "column", pt: 1}}>

        <Stack sx={{justifyContent: "space-between", flexDirection: "row", width: "100%", alignItems: "flex-end", mb: 0.5}}>
            <Typography variant="h5" sx={{fontWeight: 700, color: t1Color, width: 60, lineHeight: "1em"}}>
                {winPct}%
            </Typography>

            <Typography variant="caption" color="#999" sx={{lineHeight: "1em", fontWeight: "normal"}}>
                {t("activityCard.winPrediction")}
            </Typography>

            <Typography variant="h5" sx={{fontWeight: 700, color: "#aaa", width: 60, lineHeight: "1em", textAlign: "right"}}>
                {100 - winPct}%
            </Typography>
        </Stack>

        <LinearProgress variant="determinate" value={winPct} 
            sx={{width: "100%", borderRadius: 5, height: 10,
                    backgroundColor: "#ddd",
                    '& .MuiLinearProgress-bar': {
                        backgroundColor: t1Color
                    }
            }} 
            
            />

        </Box>

        </>
}

export function H2HSelect({data, setSearchDialogMode, isSingles}) {
  const { t } = useTranslation();

  return <Stack sx={{flexDirection: "row", gap: 0.5, px: 1, flexWrap: "wrap"}}>
            <Button variant="outlined" onClick={() => setSearchDialogMode(1)} sx={{width: "calc(50% - 4px)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} >
              {data ? t("h2h.changePlayer", {name: shortenUsername(data?.my_summary?.name, 5)}) : t("h2h.searchPlayer")}
            </Button>
            <Button variant="outlined" onClick={() => setSearchDialogMode(2)} sx={{width: "calc(50% - 4px)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} >
              {data ? t("h2h.changePlayer", {name: shortenUsername(data?.o_summary?.name, 5)}) : t("h2h.searchOpponent")}
            </Button>
            {!isSingles && <Button variant="outlined" disabled={data == null} color={data?.p_summary?.name ? "primary" : "info"} onClick={() => setSearchDialogMode(3)} sx={{width: "calc(50% - 4px)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} startIcon={data?.p_summary?.name ? null : <AddIcon style={{fontSize: "12px"}} />} >
              {data?.p_summary?.name ? t("h2h.changePlayer", {name: shortenUsername(data?.p_summary?.name, 5)}) : t("h2h.partner")}
            </Button>}
            {!isSingles && <Button variant="outlined" disabled={data == null} color={data?.op_summary?.name ? "primary" : "info"} onClick={() => setSearchDialogMode(4)} sx={{width: "calc(50% - 4px)", fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}} startIcon={data?.op_summary?.name ? null : <AddIcon style={{fontSize: "12px"}} />} >
              {data?.op_summary?.name ? t("h2h.changePlayer", {name: shortenUsername(data?.op_summary?.name, 5)}) : t("h2h.opponent")}
            </Button>}
        </Stack>
}

export function H2HFullSummary({data, isSingles}) {
  const { t } = useTranslation();
  const firstSummary = data?.my_summary;
  const secondSummary = data?.o_summary;
  const renderSecondTable = !isSingles && (data?.p_summary || data?.op_summary);
  const thirdSummary = data?.p_summary ? data.p_summary : data?.my_summary;
  const fourthSummary = data?.op_summary ? data.op_summary : data?.o_summary;

  const theme = useTheme();

  return <><H2HSummary my_summary={firstSummary} o_summary={secondSummary} isSingles={isSingles} />

        {renderSecondTable ? <H2HSummary my_summary={thirdSummary} o_summary={fourthSummary} isSingles={isSingles} /> : null}
        
        <OutlinedBox sx={{m: 1, mt: 0}}>
        <Typography variant="caption" fontWeight="bold" color="text.primary_bold" component="div" sx={{px: 1, py: 0.5}}>
          {t("h2h.recentMatches")}
        </Typography>
        <Divider sx={{width: "100%"}}/>

        <H2HRecentResults summary={data?.my_summary} isSingles={isSingles} />
        {data?.p_summary && !isSingles ? <>
          <Divider sx={{width: "100%"}}/>
          <H2HRecentResults summary={data?.p_summary} isSingles={isSingles} />
        </> : null}
        <Divider sx={{width: "100%"}}/>
        <H2HRecentResults summary={data?.o_summary} isSingles={isSingles} />
        {data?.op_summary && !isSingles ? <>
          <Divider sx={{width: "100%"}}/>
          <H2HRecentResults summary={data?.op_summary} isSingles={isSingles} />
        </> : null}
        </OutlinedBox>
        
        {((!isSingles && data?.doubles_encounters?.length > 0) || (isSingles && data?.singles_encounters?.length > 0)) && <OutlinedBox sx={{m: 1, mt: 0}}>
        <Typography variant="caption" fontWeight="bold" color="text.primary_bold" component="div" sx={{px: 1, py: 0.5}}>
          {t("h2h.previousEncounters")}
        </Typography>
        <Divider sx={{width: "100%"}}/>

        {!isSingles && data?.doubles_encounters?.map((encounter, index) => (
          <>
          {index > 0 && <Divider sx={{width: "100%"}}/>}
          <Box key={encounter.player11 + "-" + encounter.player12 + "-" + encounter.player21 + "-" + encounter.player22 + "-" + encounter.score + "-" + index} sx={{display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", p: 1}}>
            <Typography variant="caption" color="text.primary_bold" sx={{flexGrow: 1}}>
              <b style={{opacity: encounter.winner == 1 ? 1 : 0.4}}>{getFirstInitialAndLastName(encounter.player11_name)}&nbsp;/&nbsp;{getFirstInitialAndLastName(encounter.player12_name)}</b><br/><b style={{opacity: encounter.winner == 2 ? 1 : 0.4}}>{getFirstInitialAndLastName(encounter.player21_name)}&nbsp;/&nbsp;{getFirstInitialAndLastName(encounter.player22_name)}</b>
            </Typography>
            <Typography variant="caption" color="text.primary_bold" sx={{flexGrow: 1, fontWeight: "bold", textAlign: "right"}}>
                {encounter.score} <br/> <b style={{fontWeight: "normal", color: theme.palette.text.secondary}}>{encounter.date ? formatDateDifference(encounter.date) : ""}</b>
            </Typography>
          </Box>
          </>
        ))}
        {isSingles && data?.singles_encounters?.map((encounter, index) => (
          <>
          {index > 0 && <Divider sx={{width: "100%"}}/>}
          <Box key={encounter.player11 + "-" + encounter.player12 + "-" + encounter.player21 + "-" + encounter.player22 + "-" + encounter.score + "-" + index} sx={{display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "center", p: 1}}>
            <Typography variant="caption" color="text.primary_bold" sx={{flexGrow: 1}}>
              <b style={{opacity: encounter.winner == 1 ? 1 : 0.4}}>{shortenUsername(encounter.player1_name)}</b><br/><b style={{opacity: encounter.winner == 2 ? 1 : 0.4}}>{shortenUsername(encounter.player2_name)}</b>
            </Typography>
            <Typography variant="caption" color="text.primary_bold" sx={{flexGrow: 1, fontWeight: "bold", textAlign: "right"}}>
                {encounter.score} <br/> <b style={{fontWeight: "normal", color: theme.palette.text.secondary}}>{encounter.date ? formatDateDifference(encounter.date) : ""}</b>
            </Typography>
          </Box>
          </>
        ))}

        </OutlinedBox>}
        
        </>
}

export function H2HInfo() {
  const { t } = useTranslation();

  return <OutlinedBox sx={{m: 1, mt: 0}}>
        <Typography variant="caption" fontWeight="bold" color="text.primary_bold" component="div" sx={{px: 1, py: 0.5}}>
          {t("h2h.infoTitle")}
        </Typography>
        <Divider sx={{width: "100%"}}/>
        <Typography variant="caption" color="text.secondary" component="div" sx={{mx: 1, py: 0.5}}>
          {t("h2h.infoBody")}
        </Typography>
        </OutlinedBox>
}

export function H2HDialog({open, onClose, dialogData}) {
    const theme = useTheme();
    const { t } = useTranslation();


  const [value, setValue] = React.useState(0);
  const isSingles = value === 0;

  const [searchDialogMode, setSearchDialogMode] = React.useState(0); // 0 = none, 1 = change player1, 2 = change player2, 3 = change partner, 4 = change opponent

  const [myId, setMyId] = React.useState(null);
  const [opponentId, setOpponentId] = React.useState(null);
  const [partnerId, setPartnerId] = React.useState(null);
  const [opponentPartnerId, setOpponentPartnerId] = React.useState(null);

  const data = useHead2Head(myId, opponentId, partnerId, opponentPartnerId);

  React.useEffect(() => {
    if (dialogData) {
      setMyId(dialogData.player1);
      setOpponentId(dialogData.player2);
      setPartnerId(dialogData.player3);
      setOpponentPartnerId(dialogData.player4);

      if (dialogData.player3 && dialogData.player4) {
        setValue(1);
      } else {
        setValue(0);
      }
    }
  }, [dialogData]);

  


    return data && <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" sx={{
      "& .MuiDialog-container": {
        "& .MuiPaper-root": {
          width: "calc(100% - 16px)",
          margin: 0,
          borderRadius: 2,
        },
      },
    }}>
        <ActivityTab labels={[t("common.singles"), t("common.doubles")]} value={value} setValue={setValue} sx={{borderRadius: 0, mt: 0}} />

        <Box sx={{p: 1}}>
        
        <H2HHeader data={data} myId={null} partnerId={partnerId} opponentId={opponentId} opponentPartnerId={opponentPartnerId} isSingles={isSingles} />

        <Divider sx={{my: 2}}/>

        <H2HSelect data={data} setSearchDialogMode={setSearchDialogMode} isSingles={isSingles} />

        <Divider sx={{my: 2}}/>

        <H2HFullSummary data={data} isSingles={isSingles} />
        
        <H2HInfo/>

        </Box>

        <SearchPlayerDialog mode={searchDialogMode} onClickPlayer={(player) => {
          if (searchDialogMode === 1 && player) {
            setMyId(player.id);
          } else if (searchDialogMode === 2 && player) {
            setOpponentId(player.id);
          } else if (searchDialogMode === 3) {
            setPartnerId(player.id);
          } else if (searchDialogMode === 4) {
            setOpponentPartnerId(player.id);
          }
          setSearchDialogMode(0);
        }} />
            

    </Dialog>
}

export function SearchPlayerDialog({mode, gender, onClickPlayer}) {
  const { t } = useTranslation();
  const [search, setSearch] = React.useState("");

  const players = useSearch(search, true);

  React.useEffect(() => {
    if (mode === 0) {
      setSearch("");
    }
  }, [mode]);

  const theme = useTheme();

  return <Dialog open={mode !== 0} onClose={() => onClickPlayer(null)} fullWidth maxWidth="xs">     

        <Box sx={{p: 2, overflow: "scroll"}}>      
            <InputBase 
                autoFocus
                fullWidth
                placeholder={t("activityCard.searchPlayer")}
                startAdornment={<SearchIcon style={{color: "#999", marginRight: 8}} />} 
                inputProps={{sx: {textTransform: search.length > 0 ? "capitalize" : "none"}}}
                sx={{borderRadius: 1, backgroundColor: "white", border: "1px solid", borderColor: "divider", py: 0.5, px: 1}}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
            />

            
            <Box sx={{mt: 1, height: 300}}>
              {search.length  < 5 ?
            <OutlinedBox sx={{py: 1, px: 1, fontStyle: "italic", textAlign: "center", mt: 1}}>
              <Typography variant="body2" color="text.secondary">
                {t("h2h.refineSearch")}
              </Typography>
            </OutlinedBox>
            : players.filter(el => !gender || el.category.substring(0,1) === gender).slice(0,50).map((player) => (
                <Box key={player.id} sx={{display: "flex", alignItems: "center", justifyContent: "space-between", py: 1, px: 0.5, cursor: "pointer", "&:hover": {backgroundColor: "action.hover"}, borderRadius: 1}} onClick={() => {
                    onClickPlayer(player);
                }}>
                    <PlayerName name={player.name} name_club={player.name_club} rank={player.singles} color={theme.palette.primary.main} shortenUsernameLength={100} />
                    <ChevronRightIcon sx={{color: "#999"}} />
                </Box>
            ))}
            </Box>

            </Box> 
            
            <DialogActions>
            <Button onClick={() => onClickPlayer(null)}>{t("common.close")}</Button>
            </DialogActions>
        </Dialog>
        
}